import React from 'react';
import { Box } from '@mui/material';
import { DragDropContext, Draggable, Droppable, type DropResult } from '@hello-pangea/dnd';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useSelector } from 'store';
import menuItems from 'menu-items';
import NavItem, {
  buildBerryUnifiedNavLinkTo,
  collectViewMenuIds,
  computeIsChatsViewsDirectLink,
  menuUrlMatchesLocation,
} from '../MenuList/NavItem';
import { useChatSidebarLayout } from 'contexts/ChatSidebarLayoutContext';
import {
  appendSendFailedToViewUrl,
  isAwaitingCustomerSendFailedFilterActive,
  sendFailedCountIdForScope,
  stripSendFailedFromViewUrl,
} from 'utils/awaiting-customer-send-failed';
import {
  buildUnifiedListItem,
  isUnifiedRowVisibleInFolder,
  SCOPES_WITHOUT_INLINE_UNASSIGNED,
  type UnifiedListKind,
  type UnifiedStatusKind,
  type ChatSidebarScopeTab,
} from 'utils/chat-sidebar-unified-statuses';
import { useViewCounts } from 'hooks/useViewCounts';
import type { NavItemType } from 'types';
import { useGetMenuMaster } from 'api/menu';
import { useChatSidebarSystemOrder } from 'hooks/useChatSidebarSystemOrder';
import { useChatSidebarTeamOrder } from 'hooks/useChatSidebarTeamOrder';
import type { DefaultChatSidebarViewId, TeamChatSidebarViewId } from 'utils/default-chat-sidebar-views';
import {
  chatSidebarBuiltinFolderTitle,
  type ChatFolderTitle,
} from 'utils/chat-view-context-title';

const DEFAULT_VIEW_ID_TO_KIND: Record<DefaultChatSidebarViewId, UnifiedStatusKind> = {
  'unassigned-chats': 'unassigned',
  'newly-assigned': 'firstReply',
  'awaiting-me': 'awaitingReply',
  'waiting-customer': 'awaitingCustomer',
  'my-chats-snoozed': 'reminder',
  'chats-completed': 'completed',
};

const TEAM_VIEW_ID_TO_KIND: Record<TeamChatSidebarViewId, UnifiedStatusKind> = {
  'team-unassigned-chats': 'unassigned',
  'team-newly-assigned': 'firstReply',
  'team-awaiting-me': 'awaitingReply',
  'team-waiting-customer': 'awaitingCustomer',
  'team-my-chats-snoozed': 'reminder',
  'team-chats-completed': 'completed',
};

function orderedKindsForScope(
  scope: ChatSidebarScopeTab,
  systemIds: DefaultChatSidebarViewId[],
  teamIds: TeamChatSidebarViewId[],
): UnifiedStatusKind[] {
  if (scope === 'team') {
    return teamIds.map((id) => TEAM_VIEW_ID_TO_KIND[id]);
  }
  return systemIds.map((id) => DEFAULT_VIEW_ID_TO_KIND[id]);
}

function draggableIdForKind(scope: ChatSidebarScopeTab, kind: UnifiedStatusKind): string {
  if (scope === 'team') {
    const id = (Object.entries(TEAM_VIEW_ID_TO_KIND) as [TeamChatSidebarViewId, UnifiedStatusKind][]).find(
      ([, k]) => k === kind,
    )?.[0];
    return id ?? `team-${kind}`;
  }
  const id = (Object.entries(DEFAULT_VIEW_ID_TO_KIND) as [DefaultChatSidebarViewId, UnifiedStatusKind][]).find(
    ([, k]) => k === kind,
  )?.[0];
  return id ?? kind;
}

type BerryOpt = { isSelected: boolean; to: string };

type UnifiedRowProps = {
  kind: UnifiedListKind;
  item: NavItemType;
  berryOptimized: BerryOpt;
  scope: ChatSidebarScopeTab;
  folderTitle: ChatFolderTitle;
  sendFailedCount?: number;
  sendFailedFilterActive?: boolean;
  sendFailedFilterTo?: string;
};

const UnifiedRow = React.memo(function UnifiedRow({
  kind,
  item,
  berryOptimized,
  scope,
  folderTitle,
  sendFailedCount,
  sendFailedFilterActive,
  sendFailedFilterTo,
}: UnifiedRowProps) {
  const counts = useViewCounts(scope, kind);
  return (
    <NavItem
      item={item}
      level={1}
      derivedUnreadCount={counts.unread}
      derivedTotalCount={counts.total}
      derivedSendFailedCount={kind === 'awaitingCustomer' ? sendFailedCount : undefined}
      sendFailedFilterActive={kind === 'awaitingCustomer' ? sendFailedFilterActive : undefined}
      sendFailedFilterTo={kind === 'awaitingCustomer' ? sendFailedFilterTo : undefined}
      berryChatSidebarItem
      berryOptimized={berryOptimized}
      chatSidebarFolderTitle={folderTitle}
    />
  );
});

type BuiltinProps = {
  systemOrder: ReturnType<typeof useChatSidebarSystemOrder>;
  teamOrder: ReturnType<typeof useChatSidebarTeamOrder>;
  /** טווח התיקייה שבה הרשימה מוצגת — לא scope גלובלי מה-URL (מונע סימון כפול בין תיקיות) */
  folderScope: ChatSidebarScopeTab;
  /** תפריט מכווץ: «לשיוך לנציג» כבר מוצגת כשורה עצמאית — מונע הופעה כפולה */
  suppressUnassignedRow?: boolean;
};

const BuiltinScopeList = React.memo(function BuiltinScopeList({
  systemOrder,
  teamOrder,
  folderScope,
  suppressUnassignedRow = false,
}: BuiltinProps) {
  //* נראות לפי התיקייה של הרשימה הזו — לא לפי הטאב הפעיל. קריאת statusVisibility
  //* (טאב פעיל) כאן הייתה הבאג שגרם לשינוי בתיקייה אחת להשפיע גם על השנייה.
  const { perScopeStatusVisibility } = useChatSidebarLayout();
  const folderStatusVisibility = perScopeStatusVisibility[folderScope];
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;

  const { pathname, search } = useLocation();
  const [searchParams] = useSearchParams();
  const selectedChatUserId = useSelector((s) => s.chat.selectedChatUser?.UserDisplayPhoneNumber);
  const userViewsConfig = useSelector((s) => s.userConfig.userViewsConfig);
  const customViewCounts = useSelector((s) => s.viewCounts.custom);
  const viewsPages = menuItems.items.find((i) => i.id === 'views-pages');
  const viewMenuIds = React.useMemo(() => collectViewMenuIds(viewsPages?.children), [viewsPages?.children]);

  const resolvedScope: ChatSidebarScopeTab = folderScope;
  const folderTitle = React.useMemo(
    () => chatSidebarBuiltinFolderTitle(folderScope),
    [folderScope],
  );
  const sendFailedCount = customViewCounts[sendFailedCountIdForScope(resolvedScope)]?.total ?? 0;
  const sendFailedFilterActive = isAwaitingCustomerSendFailedFilterActive(search);

  const kinds = React.useMemo(
    () => orderedKindsForScope(resolvedScope, systemOrder.orderedIds, teamOrder.orderedIds),
    [resolvedScope, systemOrder.orderedIds, teamOrder.orderedIds],
  );

  const isChatsDirect = React.useMemo(() => computeIsChatsViewsDirectLink(pathname), [pathname]);

  const { allConv, statusRows } = React.useMemo(() => {
    const rows: Array<{ kind: UnifiedListKind; item: NavItemType; berryOptimized: BerryOpt }> = [];
    const push = (kind: UnifiedListKind) => {
      if (!isUnifiedRowVisibleInFolder(folderScope, kind, folderStatusVisibility.statusRows, userViewsConfig)) {
        return;
      }
      const item = buildUnifiedListItem(resolvedScope, kind);
      const isViewPage = viewMenuIds.includes(item.id!);
      rows.push({
        kind,
        item,
        berryOptimized: {
          isSelected: menuUrlMatchesLocation(item, pathname, search, isChatsDirect, true),
          to: buildBerryUnifiedNavLinkTo(item, searchParams, selectedChatUserId, isViewPage, true),
        },
      });
    };
    push('allConversations');
    for (const k of kinds) {
      if (k === 'unassigned' && (suppressUnassignedRow || SCOPES_WITHOUT_INLINE_UNASSIGNED.includes(resolvedScope))) {
        continue;
      }
      push(k);
    }
    const allConversationsRow = rows.find((r) => r.kind === 'allConversations');
    const rest = rows.filter((r) => r.kind !== 'allConversations');
    return { allConv: allConversationsRow, statusRows: rest };
  }, [
    resolvedScope,
    kinds,
    folderScope,
    suppressUnassignedRow,
    folderStatusVisibility.statusRows,
    userViewsConfig,
    pathname,
    search,
    searchParams,
    selectedChatUserId,
    isChatsDirect,
    viewMenuIds,
  ]);

  const onDragEnd = React.useCallback(
    (result: DropResult) => {
      if (resolvedScope === 'team') {
        teamOrder.onDragEnd(result);
      } else {
        systemOrder.onDragEnd(result);
      }
    },
    [resolvedScope, systemOrder.onDragEnd, teamOrder.onDragEnd],
  );

  const renderAllConv = () =>
    allConv ? (
      <UnifiedRow
        kind={allConv.kind}
        item={allConv.item}
        berryOptimized={allConv.berryOptimized}
        scope={resolvedScope}
        folderTitle={folderTitle}
      />
    ) : null;

  const listDragRowSx = {
    width: '100%',
    borderRadius: 1,
    '&:active': { cursor: 'grabbing' },
  } as const;
  const cloneDragRowSx = {
    ...listDragRowSx,
    bgcolor: 'background.paper',
    boxShadow: 2,
  } as const;

  const renderStatusRow = (row: (typeof statusRows)[number], index: number, drag?: boolean) => {
    const awaitingItem = row.kind === 'awaitingCustomer' ? row.item : null;
    const sendFailedFilterTo = awaitingItem
      ? sendFailedFilterActive
        ? stripSendFailedFromViewUrl(awaitingItem.url!)
        : appendSendFailedToViewUrl(awaitingItem.url!)
      : undefined;

    const inner = (
      <UnifiedRow
        kind={row.kind}
        item={row.item}
        berryOptimized={row.berryOptimized}
        scope={resolvedScope}
        folderTitle={folderTitle}
        sendFailedCount={row.kind === 'awaitingCustomer' ? sendFailedCount : undefined}
        sendFailedFilterActive={row.kind === 'awaitingCustomer' ? sendFailedFilterActive : undefined}
        sendFailedFilterTo={sendFailedFilterTo}
      />
    );
    if (!drag) return <React.Fragment key={row.kind}>{inner}</React.Fragment>;
    return (
      <Draggable key={row.kind} draggableId={draggableIdForKind(resolvedScope, row.kind as UnifiedStatusKind)} index={index}>
        {(dragProvided) => (
          <Box
            ref={dragProvided.innerRef as React.Ref<HTMLDivElement>}
            {...(dragProvided.draggableProps ?? {})}
            {...(dragProvided.dragHandleProps ?? {})}
            sx={listDragRowSx}
          >
            {inner}
          </Box>
        )}
      </Draggable>
    );
  };

  const plainStatusList = <>{statusRows.map((row) => renderStatusRow(row, 0, false))}</>;

  if (!drawerOpen) {
    return (
      <Box sx={{ width: '100%' }}>
        {renderAllConv()}
        {plainStatusList}
      </Box>
    );
  }

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Box sx={{ width: '100%' }}>
        {renderAllConv()}
        <Droppable
          droppableId={`sidebar-status-${resolvedScope}`}
          renderClone={(provided, _snapshot, rubric) => {
            const row = statusRows[rubric.source.index];
            if (!row) return null;
            return (
              <Box
                ref={provided.innerRef as React.Ref<HTMLDivElement>}
                {...(provided.draggableProps ?? {})}
                {...(provided.dragHandleProps ?? {})}
                sx={cloneDragRowSx}
              >
                <UnifiedRow
                  kind={row.kind}
                  item={row.item}
                  berryOptimized={row.berryOptimized}
                  scope={resolvedScope}
                  folderTitle={folderTitle}
                />
              </Box>
            );
          }}
        >
          {(droppableProvided) => (
            <Box ref={droppableProvided.innerRef} {...droppableProvided.droppableProps} sx={{ width: '100%' }}>
              {statusRows.map((row, index) => renderStatusRow(row, index, true))}
              {droppableProvided.placeholder}
            </Box>
          )}
        </Droppable>
      </Box>
    </DragDropContext>
  );
});

type UnifiedChatStatusListProps = {
  folderScope?: ChatSidebarScopeTab;
  /** תפריט מכווץ: «לשיוך לנציג» מוצגת כשורה עצמאית ולכן מסוננת מכאן */
  suppressUnassignedRow?: boolean;
};

function UnifiedChatStatusList({ folderScope, suppressUnassignedRow }: UnifiedChatStatusListProps) {
  const systemOrder = useChatSidebarSystemOrder();
  const teamOrder = useChatSidebarTeamOrder();
  const { scope: layoutScope } = useChatSidebarLayout();
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;

  const scopeForList = folderScope ?? layoutScope;

  return (
    <Box sx={{ width: '100%', pt: drawerOpen ? 0 : 2 }}>
      <BuiltinScopeList
        systemOrder={systemOrder}
        teamOrder={teamOrder}
        folderScope={scopeForList}
        suppressUnassignedRow={suppressUnassignedRow}
      />
    </Box>
  );
}

export default React.memo(UnifiedChatStatusList);
